import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { FilterEmitter, ProductFilterItem, ProductFilterItemArray } from '../modules/filters/models/product-filter.models';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { StaticResourceService } from '@shared/services/static-resources/static-resource.service';

@Injectable()
export class ProductFilterService {
    private readonly clearFilter = new BehaviorSubject(false);

    public clearFilter$ = this.clearFilter.asObservable();

    private readonly removeFilterSubject = new Subject<ProductFilterItem>();

    public removeFilter$ = this.removeFilterSubject.asObservable();

    private readonly communicateFilterChange = new Subject<FilterEmitter>();

    public communicateFilterChange$ = this.communicateFilterChange.asObservable();

    private readonly DISCOUNT_FILTER = ['discountFilter', 'originalPriceDiscountFilter'];

    constructor(
        private readonly staticResourceService: StaticResourceService,
        private readonly translateService: TranslateService,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly platformService: CorePlatformService
    ) { }

    public updateFilterFromOutside(filter: FilterEmitter): void {
        this.communicateFilterChange.next(filter);
    }

    public getFilterDescription(filter: ProductFilterItem, filterName?: string): number | string {
        let description = '';
        if (this.DISCOUNT_FILTER.includes(filterName)) {
            const discountValue = filter.name?.slice(0, -1);
            description = this.translateService.instant(`dev.category.filters.discount.${discountValue}`);
        } else {
            description = filter.value ? filter.value : filter.name;
        }
        return description;
    }

    /**
     * retorna el nombre del filtro
     * @param filter {ProductFilterItem} ejemplo "CO-BLANCOS"
     * @returns
     */
    public getFilterName(filter: ProductFilterItem): string {
        return filter.name ? filter.name : filter.price?.toString();
    }

    /**
     * notifica que se deben limpiar los filtros seleccionados
     * @param isClear {boolean}
     */
    public setClearFilters(isClear: boolean) {
        this.clearFilter.next(isClear);
    }

    /**
     * retorna un arreglo con los filtros seleccionados
     * @param filters
     * @returns ProductFilterItem[]
     */
    public static getFilterApplied(filters: ProductFilterItemArray[]): ProductFilterItem[] {
        let allselectedFilter: ProductFilterItem[] = [];
        let selectedFilter: ProductFilterItem[] = [];
        if (filters) {
            for (const value of Object.keys(filters)) {
                selectedFilter = filters[value].filter((filter: ProductFilterItem) => filter.isChecked)
                    .map((item: ProductFilterItem) => {
                        return { ...item, filterGroup: value };
                    });
                if (selectedFilter.length !== 0) {
                    allselectedFilter = [...allselectedFilter, ...selectedFilter];
                }
            }
        }
        return allselectedFilter;
    }

    /**
     * notifica que un filtro ha sido deseleccionado
     * @param filter {ProductFilterItem}
     */
    public setRemoveFilter(filter: ProductFilterItem) {
        this.removeFilterSubject.next(filter);
    }

    /**
     * Directive for changing the background image for the corresponding color from static resources
     */
    public filtersColorCheckDrt(filtersColorCheck: string) {
        if (this.platformService.isBrowser()) {
            if (!filtersColorCheck) {
                return;
            }

            if (!this.document.querySelectorAll('style[id=colors]').length) {
                const stElement = this.document.createElement('style');
                stElement.id = 'colors';
                this.document.head.appendChild(stElement);
            }

            const styles = this.document.querySelectorAll('style[id=colors]')[0];
            const timestamp = Math.floor(Date.now() / 1000);
            const image = `colors/${filtersColorCheck}.jpg`;
            const imageUrl = this.staticResourceService.getImage(image, timestamp);
            // console.log(imageUrl, inditex.getStaticResourceUrl('/images/colors/' + filtersColorCheck + '.jpg'));
            styles.innerHTML = [
                styles.innerHTML,
                `.color-filter .color-box.${filtersColorCheck} {`,
                `background: url(${encodeURI(imageUrl)}) !important;`,
                'background-size: cover !important;',
                '}',
            ].join('');
        }
    }

    public getProductIdsFiltered(filters, defaultOrders) {
        const filtersApplied = ProductFilterService.getFilterApplied(filters.filters);

        const filterGroupProducts: Record<string, number[]> = {};
        // biome-ignore lint/complexity/noForEach: <explanation>
        filtersApplied.forEach((_filter) => {
            if (!filterGroupProducts[_filter.filterGroup]) {
                filterGroupProducts[_filter.filterGroup] = [];
            }
            filterGroupProducts[_filter.filterGroup] = [
                ...filterGroupProducts[_filter.filterGroup],
                ..._filter.productIds,
            ];
        });

        if (Object.values(filterGroupProducts).length === 0) {
            return defaultOrders[filters.order.orderType];
        }

        const filteredProducts = Object.values(filterGroupProducts).reduce((acc, curr) => {
            return acc.filter((i) => curr.includes(i));
        });

        const productsId = defaultOrders[filters.order.orderType]
            .filter(productId => filteredProducts.includes(productId));

        return productsId;
    }
}
